import React from 'react'
import { FormattedMessage } from 'react-intl'
import messages from '../../../messages'
import { makeStyles, Theme } from '@material-ui/core/styles'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import { Grid, Box } from '@material-ui/core'
import { Formik, Field } from 'formik'
import { PerPage, SortBy } from '../Enum'
import { semiConfigContext } from '../../shrna/Shrna'
import { initialState } from '../Reducer'
import { useShrnaSearchTermLazyQuery } from '@src/queries/ShrnaProductSearchQuery.generated'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import { SemiConfiguratorValues } from '../../../utils/constants'

const useStyles = makeStyles((theme: Theme) => {
  return {
    lightGreyFont: {
      padding: theme.spacing(5.5, 1, 3.5),
      fontSize: theme.typography.pxToRem(15),
      '& .MuiGrid-spacing-xs-2': {
        paddingTop: theme.spacing(3.5),
      },
    },
    labelStylePerPage: {
      fontSize: theme.typography.pxToRem(15),
      paddingTop: theme.spacing(5.5),
      paddingLeft: theme.spacing(2),
      '& .MuiGrid-spacing-xs-2': {
        paddingTop: theme.spacing(3.5),
      },
    },
    labelStyleSortBy: {
      fontSize: theme.typography.pxToRem(15),
      paddingTop: theme.spacing(5.5),
      paddingLeft: theme.spacing(3),
      '& .MuiGrid-spacing-xs-2': {
        paddingTop: theme.spacing(3.5),
      },
    },
    valueStyle: {
      paddingTop: theme.spacing(3.5),
    },
    valueStylePerPage: {
      paddingTop: theme.spacing(3.5),
      paddingRight: theme.spacing(1.5),
    },
    paddingGrid: {
      padding: theme.spacing(1, 0, 1),
    },
    bgColor: {
      '& .MuiSelect-select': {
        border: 'none',
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
    heading: {
      fontSize: theme.typography.pxToRem(12),
      paddingLeft: theme.spacing(0),
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing(2),
    },
  }
})

const ShrnaClonesResultSet = () => {
  const classes = useStyles()
  const [sortBy, setSortBy] = React.useState('TRC Clone Id  ASC')
  const { state, dispatch } = React.useContext(semiConfigContext)
  const [getShrnaSearchTerm, { data, loading }] = useShrnaSearchTermLazyQuery()
  const countForAllData = state.searchResponseJson.count
  const perPage = state.searchValue.count
  const totalCount = state.paginationData.totalCount
  const page = state.paginationData.page

  const searchResult = async (obj): Promise<void> => {
    await getShrnaSearchTerm({ variables: { input: obj } })
  }

  const setInitialState = (): void => {
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: initialState.shrnaDropDownValue,
    })
    dispatch({
      type: 'UPDATE_DROPDOWN_RESPONSE',
      data: initialState.shrnaDropDownResponseList,
    })
    dispatch({
      type: 'UPDATE_PRICE_CALL_RESPONSE',
      data: initialState.priceCallResponse,
    })
    dispatch({
      type: 'UPDATE_DROP_DOWN',
      data: initialState.shrnaDropDownValue,
    })
    dispatch({
      type: 'UPDATE_PRICE_CALL',
      data: initialState.priceCallData,
    })
    dispatch({
      type: 'UPDATE_SPECIFICATION_VALUES',
      data: initialState.specificationValues,
    })
  }

  const onPerPageChange = (event): void => {
    dispatch({ type: 'SET_IS_ALL_CHECKED', data: false })
    dispatch({ type: 'SET_PAGINATION_PAGE', data: 1 })
    if (state.stepNo !== 1) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
    }
    setInitialState()
    const value = event.target.value
    let strToNumPerPage = 10
    dispatch({ type: 'SET_PER_PAGE_COUNT', data: value })
    if (value === PerPage.PerPageALL) {
      strToNumPerPage = countForAllData
    } else {
      strToNumPerPage = parseInt(value)
    }
    const perPageData = {
      ...state.searchValue,
      count: strToNumPerPage,
      page: 1,
    }
    dispatch({
      type: 'UPDATE_SEARCH_TERM',
      data: { ...state.searchValue, count: strToNumPerPage, page: 1 },
    })
    dispatch({ type: 'SET_IS_ALL_CHECKED', data: false })
    searchResult(perPageData)
  }

  const onSortByChange = (event): void => {
    dispatch({ type: 'SET_IS_ALL_CHECKED', data: false })
    dispatch({ type: 'SET_PAGINATION_PAGE', data: 1 })
    if (state.stepNo !== 1) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
      setInitialState()
    }
    setSortBy(event.target.value)
    const value = event.target.value
    let sortByValue = ''
    let orderValue = ''
    if (value === SortBy.trcCloneIdAsc) {
      sortByValue = SortBy.trcId
      orderValue = SortBy.ASC
    } else if (value === SortBy.trcCloneIdDsc) {
      sortByValue = SortBy.trcId
      orderValue = SortBy.DESC
    } else if (value === SortBy.geneSymAsc) {
      sortByValue = SortBy.geneSymbol
      orderValue = SortBy.ASC
    } else if (value === SortBy.geneSymDsc) {
      sortByValue = SortBy.geneSymbol
      orderValue = SortBy.DESC
    } else if (value === SortBy.validatedAsc) {
      sortByValue = SortBy.validated
      orderValue = SortBy.ASC
    } else if (value === SortBy.validatedDsc) {
      sortByValue = SortBy.validated
      orderValue = SortBy.DESC
    } else if (value === SortBy.regionAsc) {
      sortByValue = SortBy.region
      orderValue = SortBy.ASC
    } else if (value === SortBy.regionDsc) {
      sortByValue = SortBy.region
      orderValue = SortBy.DESC
    } else if (value === SortBy.targetSequenceAsc) {
      sortByValue = SortBy.targetSeq
      orderValue = SortBy.ASC
    } else if (value === SortBy.targetSequenceDsc) {
      sortByValue = SortBy.targetSeq
      orderValue = SortBy.DESC
    }
    const sortByData = {
      ...state.searchValue,
      sort: sortByValue,
      order: orderValue,
      page: 1,
    }
    dispatch({
      type: 'UPDATE_SEARCH_TERM',
      data: {
        ...state.searchValue,
        sort: sortByValue,
        order: orderValue,
        page: 1,
      },
    })
    searchResult(sortByData)
  }

  React.useEffect(() => {
    if (data !== undefined) {
      if (!data.getShrnaSearchTerm?.serverError) {
        dispatch({
          type: 'UPDATE_SEARCH_RESPONSE',
          data: data.getShrnaSearchTerm,
        })
        if (
          data.getShrnaSearchTerm.count !== null &&
          data.getShrnaSearchTerm.count !== undefined
        ) {
          dispatch({
            type: 'UPDATE_PAGINATION_DATA',
            data: {
              ...state.paginationData,
              totalCount: data.getShrnaSearchTerm.count,
            },
          })
        }
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: SemiConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  return (
    <>
      <Box>
        <Formik
          initialValues={{
            materialName: (
              <FormattedMessage
                {...messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL_SYNTHETIC_RNA_crisprgrna}
              />
            ),
          }}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              actions.setSubmitting(false)
            }, 1000)
          }}
        >
          {() => (
            <>
              <Grid container className={classes.paddingGrid} direction="row">
                <Grid item xs={3} className={classes.lightGreyFont}>
                  <FormattedMessage
                    {...messages.SEMICONF_PAGINATION_SHOWING_TEXT_crisprgrna}
                  />{' '}
                  {totalCount > 0 ? (page - 1) * perPage + 1 : 0} -{' '}
                  {totalCount > page * perPage ? page * perPage : totalCount}{' '}
                  <FormattedMessage
                    {...messages.SEMICONF_PAGINATION_OF_TEXT_crisprgrna}
                  />{' '}
                  {totalCount}{' '}
                  <FormattedMessage
                    {...messages.SEMICONF_PAGINATION_RESULTS_TEXT_crisprgrna}
                  />
                </Grid>
                <Grid item xs={3} className={classes.heading}></Grid>
                <Grid item xs={1} className={classes.labelStylePerPage}>
                  <FormattedMessage
                    {...messages.SEMI_CONF_PER_PAGE_crispgrna}
                  />
                </Grid>
                <Grid item xs={1} className={classes.valueStylePerPage}>
                  <Field
                    name="perPage"
                    component={LiquidSelectAdapter}
                    options={[
                      { label: PerPage.PerPage10, value: PerPage.PerPage10 },
                      { label: PerPage.PerPage20, value: PerPage.PerPage20 },
                      { label: PerPage.PerPage30, value: PerPage.PerPage30 },
                      { label: PerPage.PerPage50, value: PerPage.PerPage50 },
                      { label: PerPage.PerPage100, value: PerPage.PerPage100 },
                      { label: PerPage.PerPageALL, value: PerPage.PerPageALL },
                    ]}
                    value={state.perPageCount}
                    onChange={onPerPageChange}
                    className={classes.bgColor}
                  />
                </Grid>
                <Grid item xs={1} className={classes.labelStyleSortBy}>
                  <FormattedMessage {...messages.SEMI_CONF_SORTBY_crispgrna} />
                </Grid>
                <Grid item xs={3} className={classes.valueStyle}>
                  <Field
                    name="sortBy"
                    component={LiquidSelectAdapter}
                    value={sortBy}
                    options={[
                      {
                        label: SortBy.trcCloneIdAsc,
                        value: SortBy.trcCloneIdAsc,
                      },
                      {
                        label: SortBy.trcCloneIdDsc,
                        value: SortBy.trcCloneIdDsc,
                      },
                      {
                        label: SortBy.geneSymAsc,
                        value: SortBy.geneSymAsc,
                      },
                      {
                        label: SortBy.geneSymDsc,
                        value: SortBy.geneSymDsc,
                      },
                      {
                        label: SortBy.validatedAsc,
                        value: SortBy.validatedAsc,
                      },
                      {
                        label: SortBy.validatedDsc,
                        value: SortBy.validatedDsc,
                      },
                      {
                        label: SortBy.regionAsc,
                        value: SortBy.regionAsc,
                      },
                      {
                        label: SortBy.regionDsc,
                        value: SortBy.regionDsc,
                      },
                      {
                        label: SortBy.targetSequenceAsc,
                        value: SortBy.targetSequenceAsc,
                      },
                      {
                        label: SortBy.targetSequenceDsc,
                        value: SortBy.targetSequenceDsc,
                      },
                    ]}
                    onChange={onSortByChange}
                    className={classes.bgColor}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Formik>
      </Box>
      {loading && <CircleLoader />}
    </>
  )
}

export default ShrnaClonesResultSet
